import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, UploadCloudIcon, GlobeIcon, PlusIcon, ArrowUpIcon, CheckCircle2Icon, ImageIcon, AlertCircleIcon, FileSpreadsheetIcon, LinkIcon, TagIcon, PencilIcon } from 'lucide-react';
type MessageType = 'greeting' | 'upload_prompt' | 'upload_processing' | 'upload_result' | 'url_prompt' | 'url_processing' | 'url_result' | 'single_result' | 'image_prompt' | 'excel_summary' | 'text';
type FlowStep = 'idle' | 'awaiting_single_desc' | 'awaiting_vehicle_details' | 'awaiting_vehicle_images' | 'awaiting_excel_context' | 'awaiting_excel_rules';
interface DraftItem {
  title: string;
  price: string;
  description: string;
}
interface Message {
  id: string;
  role: 'ai' | 'user';
  content: string;
  type: MessageType;
  draft?: DraftItem;
}
interface CatalogChatProps {
  catalogName: string;
  onPopulateDemo: () => void;
}
export function CatalogChat({
  catalogName,
  onPopulateDemo
}: CatalogChatProps) {
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    role: 'ai',
    content: `Hi 👋 I'm Talab AI. Let's build your ${catalogName}. How would you like to start?`,
    type: 'greeting'
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [flowStep, setFlowStep] = useState<FlowStep>('idle');
  const [draftItem, setDraftItem] = useState<DraftItem | null>(null);
  const [vehicleDetails, setVehicleDetails] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, isTyping]);
  const addMessage = (role: 'ai' | 'user', content: string, type: MessageType = 'text', draft?: DraftItem) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
      role,
      content,
      type,
      draft
    }]);
  };
  // Knowledge base for technical questions
  const answerQuestion = (text: string): string => {
    const t = text.toLowerCase();
    if (t.includes('publish')) {
      return 'When you publish, Talab structures each listing, writes an optimized title and description, and makes it discoverable in AI search and customer chat. You can unpublish anytime.';
    }
    if (t.includes('photo') || t.includes('picture') || t.includes('image')) {
      return 'Photos help your listings match better and build buyer trust. Items without photos stay as drafts until you add one — or you can publish without photos and add them later.';
    }
    if (t.includes('sync') || t.includes('website') || t.includes('often')) {
      return 'I crawl your website and keep it in sync daily. You can also trigger a manual re-sync anytime, and I only import items I can read cleanly.';
    }
    if (t.includes('api') || t.includes('integration') || t.includes('erp')) {
      return 'I support REST APIs and most ERPs for real-time inventory sync. I just need your endpoint and an API key — all credentials are encrypted.';
    }
    if (t.includes('data') || t.includes('safe') || t.includes('secure') || t.includes('privacy')) {
      return 'Your data is encrypted and only used to power your own catalog and AI assistant. It is never shared with other businesses.';
    }
    if (t.includes('excel') || t.includes('csv') || t.includes('format') || t.includes('column')) {
      return 'I accept .xlsx, .xls, and .csv files. There is no strict template — I automatically detect and map your columns to the catalog format.';
    }
    return null as unknown as string;
  };
  const buildVehicleDraft = (details: string): DraftItem => {
    const title = details.length > 48 ? details.slice(0, 48).trim() + '…' : details;
    return {
      title: title.charAt(0).toUpperCase() + title.slice(1),
      price: 'AED — / day',
      description: `AI-optimized vehicle listing generated from the details you shared (${details}). I've highlighted the make, model, year, and mileage so it ranks well in search and customer chat.`
    };
  };
  const finishVehicleListing = (hasImages: boolean) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const draft = buildVehicleDraft(vehicleDetails);
      setDraftItem(draft);
      addMessage('ai', hasImages ? "Perfect — I've added your photos. Here's the draft listing for your vehicle. Review it and choose what to do next:" : "No problem, you can add photos later. Here's the draft listing for your vehicle:", 'single_result', draft);
      setFlowStep('idle');
    }, 1300);
  };
  const handleVehicleImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const count = files.length;
    addMessage('user', `Attached ${count} ${count === 1 ? 'photo' : 'photos'}`);
    e.target.value = '';
    finishVehicleListing(true);
  };
  const handleSend = () => {
    if (!inputValue.trim()) return;
    const text = inputValue.trim();
    setInputValue('');
    addMessage('user', text);
    // Excel flow: capture what the list is for
    if (flowStep === 'awaiting_excel_context') {
      const ctx = text.toLowerCase();
      const isRental = /\b(rent|rental|hire|lease|per day|daily)\b/.test(ctx);
      setFlowStep('awaiting_excel_rules');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', isRental ? 'Perfect — rentals. I can see a price per day and a price per month in your sheet. Are there any other fees or rules you want to add (e.g. delivery fee, drop-off fee, child seat, insurance)?' : 'Got it. I can see your pricing columns. Are there any other fees or rules you want to add (e.g. delivery, deposit, or any add-ons)?');
      }, 1100);
      return;
    }
    // Excel flow: capture fees/rules, then show the import summary
    if (flowStep === 'awaiting_excel_rules') {
      setFlowStep('idle');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "Done — I've applied those fees and rules to all matching items, and automatically categorized them based on their descriptions. Here's a summary of your import:", 'excel_summary');
      }, 1300);
      return;
    }
    // Vehicle sell flow: capture details, then ask for images
    if (flowStep === 'awaiting_vehicle_details') {
      setVehicleDetails(text);
      setFlowStep('awaiting_vehicle_images');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "Great, I've got the make, model, year, and mileage. Now please share a few photos of the vehicle — clear shots of the exterior, interior, and dashboard help your listing perform best.", 'image_prompt');
      }, 1100);
      return;
    }
    // Detect "sell my car / vehicle" intent
    const t = text.toLowerCase();
    const sellVehicle = /\b(sell|list|add|post)\b/.test(t) && /\b(car|vehicle|truck|suv|motorcycle|motorbike|auto)\b/.test(t);
    if (flowStep === 'idle' && sellVehicle) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "I'd be happy to help you list your vehicle. To get started, please share the vehicle details — Make, Model, Year, and Mileage. You can type them all in one message (e.g. 'Toyota Prado, 2018, 95,000 km').");
        setFlowStep('awaiting_vehicle_details');
      }, 1000);
      return;
    }
    // Single-item flow: capturing the item description
    if (flowStep === 'awaiting_single_desc') {
      setFlowStep('idle');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        // Build a simple draft from the description
        const title = text.length > 48 ? text.slice(0, 48).trim() + '…' : text;
        const draft: DraftItem = {
          title: title.charAt(0).toUpperCase() + title.slice(1),
          price: 'AED — / day',
          description: `Well-presented listing generated from your description. I've written an AI-optimized summary highlighting the key details of "${text}" so it performs well in search and customer chat.`
        };
        setDraftItem(draft);
        addMessage('ai', "Here's the draft listing I prepared from your description. Review it and choose what to do next:", 'single_result', draft);
      }, 1300);
      return;
    }
    // Otherwise: answer technical questions from the knowledge base
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const answer = answerQuestion(text);
      addMessage('ai', answer || "I can help with that. Whenever you're ready to add inventory, you can upload a file, connect your website, or add a single item — just pick an option or describe what you sell.");
    }, 1000);
  };
  const handleAction = (action: string) => {
    if (action === 'excel') {
      addMessage('user', 'Upload an Excel sheet');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', 'Great! Please select your Excel (.xlsx) or CSV file. I will automatically map your columns to the catalog format.', 'upload_prompt');
      }, 800);
    } else if (action === 'api') {
      addMessage('user', 'Connect website / API integration');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', 'Perfect. Please enter your website URL or API endpoint, and I will scan and import your inventory automatically.', 'url_prompt');
      }, 800);
    } else if (action === 'single') {
      addMessage('user', 'Add my first item');
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', "Let's create your first listing. Describe the item in your own words — include the name, and any details like price, year, or condition (e.g. 'Toyota Prado 2018, GCC specs, AED 350 per day').");
        setFlowStep('awaiting_single_desc');
      }, 700);
    }
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    addMessage('user', `Attached: ${file.name}`);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'Analyzing your file and mapping the columns...', 'upload_processing');
      setTimeout(() => {
        setIsTyping(false);
        addMessage('ai', 'Got it — I can read your list and have automatically categorized the items. What is this list for? For example, are these cars for rent, products for sale, or something else?');
        setFlowStep('awaiting_excel_context');
      }, 1500);
    }, 1000);
  };
  const handleUrlSubmit = (url: string) => {
    if (!url.trim()) return;
    addMessage('user', `Connect to: ${url}`);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'Connecting to your site and syncing inventory...', 'url_processing');
      setTimeout(() => {
        addMessage('ai', 'Sync complete! I have automatically categorized your items based on their details. Here is the summary of your integration:', 'url_result');
      }, 2000);
    }, 1000);
  };
  const handlePublishAction = (action: string) => {
    addMessage('user', action);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'Done! Your catalog has been updated. You can view your active inventory now.');
      setTimeout(() => {
        onPopulateDemo();
      }, 1500);
    }, 1000);
  };
  const handleEditDraft = () => {
    addMessage('user', 'Edit the details');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMessage('ai', 'No problem — just tell me what to change (e.g. the price, title, or any detail) and I will update the draft.');
      setFlowStep('awaiting_single_desc');
    }, 800);
  };
  return <div className="flex flex-col flex-1 h-full min-h-[420px] bg-slate-50/50 rounded-3xl border border-slate-200 overflow-hidden relative" data-id="element-951">
      {/* Chat Area */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6" data-id="element-952">
        <AnimatePresence initial={false} data-id="element-953">
          {messages.map(msg => <motion.div key={msg.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-954">
              {msg.role === 'ai' ? <div className="flex gap-4 max-w-2xl" data-id="element-955">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md mt-1" data-id="element-956">
                    <SparklesIcon className="w-5 h-5 text-white" data-id="element-957" />
                  </div>
                  <div className="space-y-3" data-id="element-958">
                    <div className="bg-white rounded-2xl rounded-tl-none p-5 shadow-soft border border-slate-100" data-id="element-959">
                      <p className="text-slate-700 leading-relaxed" data-id="element-960">
                        {msg.content}
                      </p>
                    </div>

                    {/* Dynamic AI Content based on message type */}
                    {msg.type === 'greeting' && <div className="flex flex-wrap gap-2 pt-2" data-id="element-961">
                        <button onClick={() => handleAction('excel')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-962">
                          <UploadCloudIcon className="w-4 h-4" data-id="element-963" /> Upload an
                          Excel sheet
                        </button>
                        <button onClick={() => handleAction('api')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-964">
                          <GlobeIcon className="w-4 h-4" data-id="element-965" /> Connect website /
                          API
                        </button>
                        <button onClick={() => handleAction('single')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-966">
                          <PlusIcon className="w-4 h-4" data-id="element-967" /> Add my first item
                        </button>
                      </div>}

                    {msg.type === 'upload_prompt' && <div className="pt-2" data-id="element-968">
                        <input type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" className="hidden" ref={fileInputRef} onChange={handleFileUpload} data-id="element-969" />
                        <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-5 py-3 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-970">
                          <FileSpreadsheetIcon className="w-4 h-4" data-id="element-971" /> Select
                          File
                        </button>
                      </div>}

                    {msg.type === 'url_prompt' && <div className="pt-2 flex gap-2 max-w-md" data-id="element-972">
                        <div className="relative flex-1" data-id="element-973">
                          <LinkIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-974" />
                          <input type="url" id="url-input" placeholder="https://yourwebsite.com" className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20 outline-none" onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleUrlSubmit(e.currentTarget.value);
                    }
                  }} data-id="element-975" />
                        </div>
                        <button onClick={() => {
                  const input = document.getElementById('url-input') as HTMLInputElement;
                  if (input && input.value) handleUrlSubmit(input.value);
                }} className="px-4 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-976">
                          Connect
                        </button>
                      </div>}

                    {/* Vehicle image upload prompt */}
                    {msg.type === 'image_prompt' && <div className="pt-2 flex flex-wrap gap-2" data-id="element-977">
                        <input type="file" accept="image/*" multiple className="hidden" ref={imageInputRef} onChange={handleVehicleImages} data-id="element-978" />
                        <button onClick={() => imageInputRef.current?.click()} className="flex items-center gap-2 px-5 py-3 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-979">
                          <ImageIcon className="w-4 h-4" data-id="element-980" /> Upload photos
                        </button>
                        <button onClick={() => {
                  addMessage('user', 'Skip photos for now');
                  finishVehicleListing(false);
                }} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-981">
                          Skip for now
                        </button>
                      </div>}

                    {/* Single item draft preview */}
                    {msg.type === 'single_result' && msg.draft && <div className="space-y-4 pt-2" data-id="element-982">
                        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm max-w-md" data-id="element-983">
                          <div className="flex items-center gap-2 text-xs font-bold text-primary-indigo uppercase tracking-wider mb-3" data-id="element-984">
                            <SparklesIcon className="w-3.5 h-3.5" data-id="element-985" /> AI Draft
                            Listing
                          </div>
                          <h4 className="font-bold text-dark-navy text-lg mb-1" data-id="element-986">
                            {msg.draft.title}
                          </h4>
                          <div className="flex items-center gap-1.5 text-primary-indigo font-bold mb-3" data-id="element-987">
                            <TagIcon className="w-4 h-4" data-id="element-988" />
                            {msg.draft.price}
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-989">
                            {msg.draft.description}
                          </p>
                          <div className="mt-4 flex items-center gap-2 text-xs text-amber-600 font-medium" data-id="element-990">
                            <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-991" /> No photo
                            yet — add one for the best results.
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2" data-id="element-992">
                          <button onClick={() => handlePublishAction('Publish this listing now')} className="flex items-center gap-2 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-993">
                            <CheckCircle2Icon className="w-4 h-4" data-id="element-994" /> Publish now
                          </button>
                          <button onClick={() => handlePublishAction('Add a photo before publishing')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-995">
                            <ImageIcon className="w-4 h-4" data-id="element-996" /> Add a photo
                          </button>
                          <button onClick={handleEditDraft} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-997">
                            <PencilIcon className="w-4 h-4" data-id="element-998" /> Edit details
                          </button>
                        </div>
                      </div>}

                    {/* Excel import summary with publish choices */}
                    {msg.type === 'excel_summary' && <div className="space-y-4 pt-2" data-id="element-999">
                        <div className="grid grid-cols-3 gap-3 max-w-lg" data-id="element-1000">
                          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1001">
                            <div className="flex items-center gap-1.5 text-slate-500 font-medium text-xs" data-id="element-1002">
                              <CheckCircle2Icon className="w-3.5 h-3.5 text-primary-indigo" data-id="element-1003" />{' '}
                              Cars added
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1004">
                              24
                            </div>
                          </div>
                          <div className="bg-white border border-green-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1005">
                            <div className="flex items-center gap-1.5 text-green-600 font-medium text-xs" data-id="element-1006">
                              <ImageIcon className="w-3.5 h-3.5" data-id="element-1007" /> With photos
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1008">
                              18
                            </div>
                          </div>
                          <div className="bg-white border border-amber-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1009">
                            <div className="flex items-center gap-1.5 text-amber-600 font-medium text-xs" data-id="element-1010">
                              <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-1011" /> No
                              photos
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1012">
                              6
                            </div>
                          </div>
                        </div>

                        <p className="text-sm text-slate-500 leading-relaxed max-w-lg" data-id="element-1013">
                          The 6 cars without photos can still be found in your
                          inventory as drafts — you can add photos to them
                          anytime to publish them.
                        </p>

                        <div className="flex flex-wrap gap-2" data-id="element-1014">
                          <button onClick={() => handlePublishAction('Publish all 24 — with and without photos')} className="flex items-center gap-2 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-1015">
                            <SparklesIcon className="w-4 h-4" data-id="element-1016" /> Publish all 24
                            (with & without photos)
                          </button>
                          <button onClick={() => handlePublishAction('Publish only the 18 cars with photos')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1017">
                            Publish only the 18 with photos
                          </button>
                        </div>
                      </div>}

                    {(msg.type === 'upload_result' || msg.type === 'url_result') && <div className="space-y-4 pt-2" data-id="element-1018">
                        <div className="grid grid-cols-2 gap-3" data-id="element-1019">
                          <div className="bg-white border border-green-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1020">
                            <div className="flex items-center gap-2 text-green-600 font-medium text-sm" data-id="element-1021">
                              <CheckCircle2Icon className="w-4 h-4" data-id="element-1022" />{' '}
                              Successfully Added
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1023">
                              42
                            </div>
                          </div>
                          <div className="bg-white border border-amber-100 rounded-xl p-4 shadow-sm flex flex-col gap-1" data-id="element-1024">
                            <div className="flex items-center gap-2 text-amber-600 font-medium text-sm" data-id="element-1025">
                              <AlertCircleIcon className="w-4 h-4" data-id="element-1026" /> Needs
                              Pictures
                            </div>
                            <div className="text-2xl font-display font-bold text-dark-navy" data-id="element-1027">
                              12
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2" data-id="element-1028">
                          <button onClick={() => handlePublishAction('Publish 30 ready items now')} className="flex items-center gap-2 px-4 py-2 bg-primary-indigo text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-1029">
                            <SparklesIcon className="w-4 h-4" data-id="element-1030" /> Publish 30
                            ready items
                          </button>
                          <button onClick={() => handlePublishAction('Publish all without pictures')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1031">
                            Publish all without pictures
                          </button>
                          <button onClick={() => handlePublishAction('I will add pictures first')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors shadow-sm" data-id="element-1032">
                            <ImageIcon className="w-4 h-4" data-id="element-1033" /> Add pictures
                          </button>
                        </div>
                      </div>}
                  </div>
                </div> : <div className="bg-primary-indigo text-white rounded-2xl rounded-tr-none p-4 max-w-md shadow-soft" data-id="element-1034">
                  <p className="leading-relaxed" data-id="element-1035">{msg.content}</p>
                </div>}
            </motion.div>)}

          {isTyping && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex gap-4 max-w-2xl" data-id="element-1036">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md mt-1" data-id="element-1037">
                <SparklesIcon className="w-5 h-5 text-white" data-id="element-1038" />
              </div>
              <div className="bg-white rounded-2xl rounded-tl-none p-5 shadow-soft border border-slate-100 flex items-center gap-1.5 h-[60px]" data-id="element-1039">
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-1040" />
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-1041" />
                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-1042" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Composer — pinned, always visible */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-slate-100" data-id="element-1043">
        <div className="max-w-4xl mx-auto relative flex items-center" data-id="element-1044">
          <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') handleSend();
        }} placeholder={flowStep === 'awaiting_vehicle_details' ? 'Make, model, year, mileage…' : flowStep === 'awaiting_single_desc' ? 'Describe your item…' : 'Ask Talab a question or describe what you want to add...'} className="w-full pl-5 pr-14 py-3.5 bg-slate-50 border border-slate-200 rounded-full text-slate-700 placeholder-slate-400 focus:outline-none focus:border-primary-indigo focus:ring-1 focus:ring-primary-indigo transition-shadow" data-id="element-1045" />
          <button onClick={handleSend} disabled={!inputValue.trim()} className="absolute right-2 w-10 h-10 flex items-center justify-center bg-primary-indigo text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-primary-indigo transition-colors" data-id="element-1046">
            <ArrowUpIcon className="w-5 h-5" data-id="element-1047" />
          </button>
        </div>
      </div>
    </div>;
}